import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const aiLearningQueryKeys = {
  all: ['aiLearning'],
  lists: (params) => [...aiLearningQueryKeys.all, 'list', params],
  list: (params) => [...aiLearningQueryKeys.lists(params)],
};

export const aiModuleQueryKeys = {
  all: ['aiModule'],
  lists: (params) => [...aiModuleQueryKeys.all, 'list', params],
  list: (params) => [...aiModuleQueryKeys.lists(params)],
};

// ==================== AiLearning ====================

/**
 * AI 학습 요청 목록 조회
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.BaseAlgorithm - 기본 알고리즘
 * @param {string} params.AiModelType - AI 모델 타입
 * @param {string} params.Status - 상태 (Pending, Completed, etc.)
 * @param {string} params.Version - 버전
 * @param {number} params.Page - 페이지 번호
 * @param {number} params.PageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */
export const useAiLearningList = (params = {}, options = {}) => {
  return useQuery({
    queryKey: aiLearningQueryKeys.list(params),
    queryFn: async () => {
      const response = await api.get('/drivecore/ai/api/AiLearning', { params });
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5분
    ...options, // enabled 등의 옵션 적용
  });
};

/**
 * AI 학습 요청 추가
 * @param {Object} payload - 학습 요청 데이터
 * @param {string} payload.baseAlgorithm - 기본 알고리즘
 * @param {string} payload.aiModelType - AI 모델 타입
 * @param {string} payload.version - 버전
 * @param {number} payload.interationCount - 반복 횟수
 * @param {string} payload.startDate - 시작 날짜 (ISO 8601)
 * @param {string} payload.endDate - 종료 날짜 (ISO 8601)
 * @param {boolean} payload.isNewModel - 새 모델 여부
 */
export const useCreateAiLearning = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      return await api.post('/drivecore/ai/api/AiLearning', payload);
    },
    onSuccess: () => {
      // 학습 요청 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: aiLearningQueryKeys.all });
    },
  });
};

/**
 * AI 학습 요청 삭제
 * @param {string} aiLearningRecordSysId - 학습 요청 ID
 */
export const useDeleteAiLearning = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (aiLearningRecordSysId) => {
      return await api.delete(`/drivecore/ai/api/AiLearning/${aiLearningRecordSysId}`);
    },
    onSuccess: () => {
      // 학습 요청 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: aiLearningQueryKeys.all });
    },
  });
};

// ==================== AiModule ====================

/**
 * AI 모듈 정보 조회
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.ModuleName - 모듈명
 * @param {string} params.Version - 버전
 * @param {number} params.Page - 페이지 번호
 * @param {number} params.PageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */
export const useAiModuleList = (params = {}, options = {}) => {
  return useQuery({
    queryKey: aiModuleQueryKeys.list(params),
    queryFn: async () => {
      const response = await api.get('/drivecore/ai/api/AiModule', { params });
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5분
    ...options, // enabled 등의 옵션 적용
  });
};

/**
 * AI 모듈 삭제
 * @param {string} aiModuleInfoSysId - AI 모듈 ID
 */
export const useDeleteAiModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (aiModuleInfoSysId) => {
      return await api.delete(`/drivecore/ai/api/AiModule?AiModuleInfoSysId=${aiModuleInfoSysId}`);
    },
    onSuccess: () => {
      // AI 모듈 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: aiModuleQueryKeys.all });
    },
  });
};